/**
 * @module appStore
 * @description App Store - Data Management Layer for Preparo
 *
 * RESPONSIBILITY: Manages application data (projects, documents metadata)
 *
 * This store handles:
 * - Projects CRUD operations
 * - Documents metadata CRUD (title, dates, projectId, wordCount)
 * - Selection state (selectedProject, selectedDoc)
 * - UI state (panel widths, theme)
 * - Search and sort functionality
 *
 * RELATIONSHIP WITH editorStore:
 * - appStore manages METADATA (document info)
 * - editorStore manages CONTENT (Markdown text)
 * - Both are coordinated via stores/index.js
 */

import { writable, derived, get, type Writable, type Readable } from 'svelte/store';
import { withTransaction, batchUpdates } from './storeTransaction';
import { db, migrateFromLocalStorage } from '../services/indexedDBService';
import { loggers } from '../services/loggerService';
import { ui } from '../config';
import JSZip from 'jszip';
import { DEFAULT_PREPARO_TEMPLATES } from '../utils/defaultPreparoTemplates';
import {
  markProjectDirty,
  markPresetDirty,
  deleteProjectFromGit,
  deletePresetFromGit,
  projectIndex,
  hydrateProject,
} from '../services/gitSyncService';
import type {
  Project,
  Document,
  Image,
  Template,
  Preset,
  DocumentType,
  DocumentSection,
  CreateProjectInput,
  CreateDocumentInput,
  UpdateProjectInput,
  UpdateDocumentInput,
  CreateImageInput,
  CreateTemplateInput,
  UpdateTemplateInput,
  CreatePresetInput,
  UpdatePresetInput
} from '../types';

// Create scoped logger for store operations
const log = loggers.store;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** UI state shape */
export interface UIState {
  projectsPanelWidth: number;
  docsPanelWidth: number;
  detailsPanelVisible: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';
}

/** Document statistics */
export interface DocStatistics {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  archived: number;
}

/** Sort column options */
export type SortColumn = 'title' | 'createdAt' | 'updatedAt' | 'status';

/** Sort order options */
export type SortOrder = 'asc' | 'desc';

/** Loading state keys */
type LoadingKey = 'projects' | 'docs' | 'doc';

// ============================================================================
// BASE STORES - Global application state
// ============================================================================

/**
 * Currently selected project
 */
export const selectedProject: Writable<Project | null> = writable(null);

/**
 * Currently selected document
 */
export const selectedDoc: Writable<Document | null> = writable(null);

/**
 * Currently editing template (markdown template loaded in the editor)
 */
export const editingTemplate: Writable<Template | null> = writable(null);

/**
 * List of all projects
 */
export const projects: Writable<Project[]> = writable([]);

/**
 * List of all documents
 */
export const docs: Writable<Document[]> = writable([]);

/**
 * List of all images for the current project
 */
export const images: Writable<Image[]> = writable([]);

/**
 * List of all templates for the current project
 */
export const templates: Writable<Template[]> = writable([]);

/**
 * List of all presets
 */
export const presets: Writable<Preset[]> = writable([]);

/**
 * Currently selected preset (for editing)
 */
export const selectedPreset: Writable<Preset | null> = writable(null);

/**
 * Whether the UI is in preset mode (editing a preset instead of a project)
 */
export const presetMode: Writable<boolean> = writable(false);

/**
 * Templates belonging to the currently selected preset
 */
export const presetTemplates: Readable<Template[]> = derived(
  [selectedPreset],
  ([$selectedPreset], set) => {
    if (!$selectedPreset) {
      set([]);
      return;
    }
    // Load templates for this preset asynchronously
    db.templates.getByPreset($selectedPreset.id).then(
      (tpls) => set(tpls),
      () => set([])
    );
  },
  [] as Template[]
);

/**
 * Search filter
 */
export const searchQuery: Writable<string> = writable('');

/**
 * Current sort column
 */
export const sortColumn: Writable<SortColumn> = writable('title');

/**
 * Sort order (asc/desc)
 */
export const sortOrder: Writable<SortOrder> = writable('asc');

/**
 * User interface state
 */
export const uiState: Writable<UIState> = writable({
  projectsPanelWidth: ui.projectsPanelWidth,
  docsPanelWidth: ui.docsPanelWidth,
  detailsPanelVisible: true,
  sidebarCollapsed: false,
  theme: 'light',
});

/**
 * Loading state
 */
export const loadingState: Writable<Record<LoadingKey, boolean>> = writable({
  projects: false,
  docs: false,
  doc: false,
});

/**
 * Errors
 */
export const errors: Writable<Record<LoadingKey, string | null>> = writable({
  projects: null,
  docs: null,
  doc: null,
});

// ============================================================================
// DERIVED STORES - Computed values
// ============================================================================

/**
 * Documents filtered by search query
 */
export const filteredDocs: Readable<Document[]> = derived(
  [docs, searchQuery],
  ([$docs, $searchQuery]) => {
    if (!$searchQuery) return $docs;

    const query = $searchQuery.toLowerCase();
    return $docs.filter(doc =>
      doc.title?.toLowerCase().includes(query)
    );
  }
);

/**
 * Filtered and sorted documents
 */
export const sortedDocs: Readable<Document[]> = derived(
  [filteredDocs, sortColumn, sortOrder],
  ([$filteredDocs, $sortColumn, $sortOrder]) => {
    const sorted = [...$filteredDocs].sort((a, b) => {
      const aVal = a[$sortColumn as keyof Document];
      const bVal = b[$sortColumn as keyof Document];

      // Handle date columns (createdAt, updatedAt)
      if ($sortColumn === 'createdAt' || $sortColumn === 'updatedAt') {
        const aTime = aVal ? new Date(aVal as string).getTime() : 0;
        const bTime = bVal ? new Date(bVal as string).getTime() : 0;
        return aTime - bTime;
      }

      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal);
      }

      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Default comparison
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    });

    return $sortOrder === 'desc' ? sorted.reverse() : sorted;
  }
);

/**
 * Documents for the selected project
 */
export const projectDocs: Readable<Document[]> = derived(
  [docs, selectedProject],
  ([$docs, $selectedProject]) => {
    if (!$selectedProject) return [];
    return $docs.filter(doc => doc.projectId === $selectedProject.id);
  }
);

/**
 * Section order for document sorting
 */
const SECTION_ORDER: Record<string, number> = {
  frontmatter: 0,
  bodymatter: 1,
  backmatter: 2,
};

/**
 * Documents for the selected project, ordered by section then by order field
 * Used for cross-chapter citation processing (ibid., op. cit.)
 */
export const projectDocsOrdered: Readable<Document[]> = derived(
  projectDocs,
  ($projectDocs) => {
    return [...$projectDocs].sort((a, b) => {
      // First sort by section (frontmatter -> bodymatter -> backmatter)
      const aSection = SECTION_ORDER[a.documentSection || 'bodymatter'] ?? 1;
      const bSection = SECTION_ORDER[b.documentSection || 'bodymatter'] ?? 1;
      if (aSection !== bSection) return aSection - bSection;

      // Then sort by order field
      const aOrder = a.order ?? 0;
      const bOrder = b.order ?? 0;
      if (aOrder !== bOrder) return aOrder - bOrder;

      // Finally sort by createdAt as fallback
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return aTime - bTime;
    });
  }
);

/**
 * Document count by status
 */
export const docStatistics: Readable<DocStatistics> = derived(
  docs,
  ($docs) => {
    const stats: DocStatistics = {
      total: $docs.length,
      todo: 0,
      inProgress: 0,
      done: 0,
      archived: 0,
    };

    $docs.forEach(doc => {
      if (doc.status === 'todo') stats.todo++;
      else if (doc.status === 'in-progress') stats.inProgress++;
      else if (doc.status === 'done') stats.done++;
    });

    return stats;
  }
);

/**
 * Global loading indicator
 */
export const isLoading: Readable<boolean> = derived(
  loadingState,
  ($loadingState) => Object.values($loadingState).some(loading => loading)
);

/**
 * Error indicator
 */
export const hasErrors: Readable<boolean> = derived(
  errors,
  ($errors) => Object.values($errors).some(error => error !== null)
);

// ============================================================================
// ACTIONS - Centralized business logic
// ============================================================================

/**
 * Helper to wrap async operations with loading/error state management
 */
async function withLoading<T>(key: LoadingKey, fn: () => Promise<T>): Promise<T> {
  loadingState.update(s => ({ ...s, [key]: true }));
  errors.update(e => ({ ...e, [key]: null }));

  try {
    return await fn();
  } catch (error) {
    log.error(`Error in ${key}`, { error });
    errors.update(e => ({ ...e, [key]: (error as Error).message }));
    throw error;
  } finally {
    loadingState.update(s => ({ ...s, [key]: false }));
  }
}

/** App actions interface */
export interface AppActions {
  loadProjects(): Promise<Project[]>;
  loadDocs(projectId?: string | null): Promise<Document[]>;
  loadImages(projectId: string): Promise<Image[]>;
  loadTemplates(projectId: string): Promise<Template[]>;
  selectProject(project: Project | null): Promise<void>;
  selectDoc(doc: Document | null): void;
  createProject(projectData: CreateProjectInput): Promise<Project>;
  createDoc(docData: CreateDocumentInput): Promise<Document>;
  updateDoc(docId: string, updates: UpdateDocumentInput): Promise<Document>;
  deleteDoc(docId: string): Promise<void>;
  addImage(imageData: CreateImageInput): Promise<Image>;
  deleteImage(imageId: string): Promise<void>;
  addTemplate(templateData: CreateTemplateInput): Promise<Template>;
  updateTemplate(templateId: string, updates: UpdateTemplateInput): Promise<Template>;
  deleteTemplate(templateId: string): Promise<void>;
  // Preset actions
  loadPresets(): Promise<Preset[]>;
  createPreset(presetData: CreatePresetInput): Promise<Preset>;
  updatePreset(presetId: string, updates: UpdatePresetInput): Promise<Preset>;
  deletePreset(presetId: string): Promise<void>;
  selectPreset(preset: Preset | null): Promise<void>;
  loadPresetTemplates(presetId: string): Promise<Template[]>;
  applyPresetToProject(presetId: string, projectId: string): Promise<void>;
  importProjectFromZip(file: File): Promise<Project>;
  // General
  setSearchQuery(query: string): void;
  setSortColumn(column: SortColumn): void;
  updateUIState(updates: Partial<UIState>): void;
  updateProject(projectId: string, updates: UpdateProjectInput): Promise<Project>;
  deleteProject(projectId: string): Promise<void>;
  reset(): void;
}

export const appActions: AppActions = {
  /**
   * Load all projects
   */
  async loadProjects(): Promise<Project[]> {
    log.debug('Loading projects');

    return withLoading('projects', async () => {
      const projectsData = await fetchProjects();
      projects.set(projectsData);
      log.info('Projects loaded', { count: projectsData.length });
      return projectsData;
    });
  },

  /**
   * Load all documents
   */
  async loadDocs(projectId: string | null = null): Promise<Document[]> {
    log.debug('Loading docs', { projectId });

    return withLoading('docs', async () => {
      const docsData = await fetchDocs(projectId);
      docs.set(docsData);
      log.info('Docs loaded', { count: docsData.length });
      return docsData;
    });
  },

  /**
   * Load images for a project
   */
  async loadImages(projectId: string): Promise<Image[]> {
    log.debug('Loading images', { projectId });

    const imagesData = await db.images.getByProject(projectId);
    images.set(imagesData);
    log.info('Images loaded', { count: imagesData.length });
    return imagesData;
  },

  /**
   * Load templates for a project
   */
  async loadTemplates(projectId: string): Promise<Template[]> {
    log.debug('Loading templates', { projectId });

    const templatesData = await db.templates.getByProject(projectId);
    templates.set(templatesData);
    log.info('Templates loaded', { count: templatesData.length });
    return templatesData;
  },

  /**
   * Select a project
   */
  async selectProject(project: Project | null): Promise<void> {
    log.debug('Selecting project', { projectId: project?.id, title: project?.title });

    // Lazy hydration: if this project is marked stale in the index, hydrate it first
    if (project) {
      const index = get(projectIndex);
      const entry = index.get(project.id);
      if (entry && (entry.hydrationState === 'stale' || entry.hydrationState === 'unknown')) {
        log.info('Lazy-hydrating project before opening', { projectId: project.id });
        loadingState.update(s => ({ ...s, docs: true }));
        try {
          const hydrationOk = await hydrateProject(project.id);
          // Refresh project from IndexedDB (hydration updated metadata, title, etc.)
          const refreshed = await db.projects.get(project.id);
          if (refreshed) {
            project = refreshed;
            // Also update the projects store so the list reflects the hydrated data
            projects.update(list => list.map(p => p.id === refreshed.id ? refreshed : p));
          }
          if (!hydrationOk) {
            log.warn('Lazy hydration failed — opening project with available data', { projectId: project.id });
          }
        } catch (err) {
          // Hydration failed but don't block project opening — show what we have
          log.error('Lazy hydration threw — opening project with stub data', {
            projectId: project.id,
            error: (err as Error).message,
          });
        } finally {
          loadingState.update(s => ({ ...s, docs: false }));
        }
      }
    }

    await withTransaction(
      [selectedProject, selectedDoc, docs, images, templates, loadingState, errors],
      async () => {
        selectedDoc.set(null);

        if (project) {
          // Load data BEFORE setting selectedProject so that reactive statements
          // in ProjectConfigPanel find $templates/$docs/$images already populated.
          await appActions.loadDocs(project.id);
          await appActions.loadImages(project.id);
          await appActions.loadTemplates(project.id);
        } else {
          docs.set([]);
          images.set([]);
          templates.set([]);
        }

        // Set selectedProject LAST — this triggers reactive statements in
        // ProjectConfigPanel (loadMetadata, loadIndesignConfigs) which read
        // $templates. All stores must be populated before this point.
        selectedProject.set(project);
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for selectProject', { error: error.message });
        }
      }
    );
  },

  /**
   * Select a document
   */
  selectDoc(doc: Document | null): void {
    log.debug('Selecting doc', { docId: doc?.id, title: doc?.title });
    selectedDoc.set(doc);
  },

  /**
   * Create a new project
   */
  async createProject(projectData: CreateProjectInput): Promise<Project> {
    log.debug('Creating project', { title: projectData.title });

    try {
      const newProject = await createProjectAPI(projectData);
      projects.update(p => [...p, newProject]);

      // Auto-create default Preparo templates for book projects
      if (projectData.projectType === 'book') {
        for (const tpl of DEFAULT_PREPARO_TEMPLATES) {
          await db.templates.add({
            projectId: newProject.id,
            filename: tpl.filename,
            templateType: 'preparo',
            templateSection: 'preparo',
            mimeType: 'text/markdown',
            content: tpl.content,
            size: tpl.content.length,
            description: tpl.description,
          });
        }
        log.debug('Default Preparo templates created', { projectId: newProject.id, count: DEFAULT_PREPARO_TEMPLATES.length });
      }

      log.info('Project created', { projectId: newProject.id });
      markProjectDirty(newProject.id, 'metadata');
      return newProject;
    } catch (error) {
      log.error('Failed to create project', { error });
      throw error;
    }
  },

  /**
   * Create a new document
   */
  async createDoc(docData: CreateDocumentInput): Promise<Document> {
    log.debug('Creating doc', { title: docData.title, projectId: docData.projectId });

    try {
      const newDoc = await createDocAPI(docData);
      docs.update(t => [...t, newDoc]);
      log.info('Doc created', { docId: newDoc.id });
      markProjectDirty(docData.projectId, 'metadata');
      return newDoc;
    } catch (error) {
      log.error('Failed to create doc', { error });
      throw error;
    }
  },

  /**
   * Update a document
   */
  async updateDoc(docId: string, updates: UpdateDocumentInput): Promise<Document> {
    log.debug('Updating doc', { docId });

    // Resolve projectId before transaction for git sync
    const docRef = get(docs).find(d => d.id === docId);

    return withTransaction(
      [docs, selectedDoc],
      async () => {
        const updatedDoc = await updateDocAPI(docId, updates);

        docs.update(t => t.map(doc =>
          doc.id === docId ? { ...doc, ...updatedDoc } : doc
        ));

        const currentDoc = get(selectedDoc);
        if (currentDoc?.id === docId) {
          selectedDoc.update(t => t ? { ...t, ...updatedDoc } : null);
        }

        log.debug('Doc updated', { docId });
        if (docRef) markProjectDirty(docRef.projectId, 'metadata');
        return updatedDoc;
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for updateDoc', { docId, error: error.message });
        }
      }
    );
  },

  /**
   * Delete a document
   */
  async deleteDoc(docId: string): Promise<void> {
    log.debug('Deleting doc', { docId });

    // Resolve projectId before transaction for git sync
    const docRef = get(docs).find(d => d.id === docId);

    return withTransaction(
      [docs, selectedDoc],
      async () => {
        await deleteDocAPI(docId);

        docs.update(t => t.filter(doc => doc.id !== docId));

        const currentDoc = get(selectedDoc);
        if (currentDoc?.id === docId) {
          selectedDoc.set(null);
        }

        log.info('Doc deleted', { docId });
        if (docRef) markProjectDirty(docRef.projectId, 'metadata');
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for deleteDoc', { docId, error: error.message });
        }
      }
    );
  },

  /**
   * Add an image to the current project
   */
  async addImage(imageData: CreateImageInput): Promise<Image> {
    log.debug('Adding image', { filename: imageData.filename });

    const newImage = await db.images.add(imageData);
    images.update(imgs => [...imgs, newImage]);
    log.info('Image added', { imageId: newImage.id, filename: newImage.filename });
    markProjectDirty(imageData.projectId, 'image');
    return newImage;
  },

  /**
   * Delete an image
   */
  async deleteImage(imageId: string): Promise<void> {
    log.debug('Deleting image', { imageId });

    const imgRef = get(images).find(i => i.id === imageId);
    await db.images.delete(imageId);
    images.update(imgs => imgs.filter(img => img.id !== imageId));
    log.info('Image deleted', { imageId });
    if (imgRef) markProjectDirty(imgRef.projectId, 'image');
  },

  /**
   * Add a template to the current project
   */
  async addTemplate(templateData: CreateTemplateInput): Promise<Template> {
    log.debug('Adding template', { filename: templateData.filename, type: templateData.templateType });

    const newTemplate = await db.templates.add(templateData);
    templates.update(tpls => [...tpls, newTemplate]);
    log.info('Template added', { templateId: newTemplate.id, filename: newTemplate.filename });
    if (templateData.presetId) {
      markPresetDirty(templateData.presetId);
    } else if (templateData.projectId) {
      markProjectDirty(templateData.projectId, 'template');
    }
    return newTemplate;
  },

  /**
   * Update a template
   */
  async updateTemplate(templateId: string, updates: UpdateTemplateInput): Promise<Template> {
    log.debug('Updating template', { templateId });

    const updatedTemplate = await db.templates.update(templateId, updates);
    templates.update(tpls => tpls.map(tpl =>
      tpl.id === templateId ? { ...tpl, ...updatedTemplate } : tpl
    ));
    log.info('Template updated', { templateId });
    if (updatedTemplate.presetId) {
      markPresetDirty(updatedTemplate.presetId);
    } else if (updatedTemplate.projectId) {
      markProjectDirty(updatedTemplate.projectId, 'template');
    }
    return updatedTemplate;
  },

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    log.debug('Deleting template', { templateId });

    const tplRef = get(templates).find(t => t.id === templateId);
    await db.templates.delete(templateId);
    templates.update(tpls => tpls.filter(tpl => tpl.id !== templateId));
    log.info('Template deleted', { templateId });
    if (tplRef?.presetId) {
      markPresetDirty(tplRef.presetId);
    } else if (tplRef?.projectId) {
      markProjectDirty(tplRef.projectId, 'template');
    }
  },

  /**
   * Set search filter
   */
  setSearchQuery(query: string): void {
    searchQuery.set(query);
  },

  /**
   * Set sort column
   */
  setSortColumn(column: SortColumn): void {
    const currentColumn = get(sortColumn);
    const currentOrder = get(sortOrder);

    if (currentColumn === column) {
      sortOrder.set(currentOrder === 'asc' ? 'desc' : 'asc');
    } else {
      sortColumn.set(column);
      sortOrder.set('asc');
    }
  },

  /**
   * Update UI state
   */
  updateUIState(updates: Partial<UIState>): void {
    uiState.update(state => ({ ...state, ...updates }));
  },

  /**
   * Update a project
   */
  async updateProject(projectId: string, updates: UpdateProjectInput): Promise<Project> {
    log.debug('Updating project', { projectId });

    return withTransaction(
      [projects, selectedProject],
      async () => {
        const updatedProject = await updateProjectAPI(projectId, updates);

        projects.update(p => p.map(project =>
          project.id === projectId ? { ...project, ...updatedProject } : project
        ));

        const currentProject = get(selectedProject);
        if (currentProject?.id === projectId) {
          selectedProject.update(p => p ? { ...p, ...updatedProject } : null);
        }

        log.info('Project updated', { projectId });
        markProjectDirty(projectId, 'metadata');
        return updatedProject;
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for updateProject', { projectId, error: error.message });
        }
      }
    );
  },

  /**
   * Delete a project
   */
  async deleteProject(projectId: string): Promise<void> {
    log.debug('Deleting project', { projectId });

    return withTransaction(
      [projects, selectedProject, selectedDoc, docs],
      async () => {
        await deleteProjectAPI(projectId);

        projects.update(p => p.filter(project => project.id !== projectId));

        const currentProject = get(selectedProject);
        if (currentProject?.id === projectId) {
          selectedProject.set(null);
          selectedDoc.set(null);
          docs.set([]);
        }

        log.info('Project deleted', { projectId });
        deleteProjectFromGit(projectId).catch(e =>
          log.error('Failed to delete project from Git', { projectId, error: (e as Error).message })
        );
      },
      {
        onRollback: (error) => {
          log.error('Transaction rolled back for deleteProject', { projectId, error: error.message });
        }
      }
    );
  },

  // ========================================================================
  // PRESET ACTIONS
  // ========================================================================

  /**
   * Load all presets
   */
  async loadPresets(): Promise<Preset[]> {
    const presetsData = await db.presets.getAll();
    presets.set(presetsData);
    log.info('Presets loaded', { count: presetsData.length });
    return presetsData;
  },

  /**
   * Create a new preset
   */
  async createPreset(presetData: CreatePresetInput): Promise<Preset> {
    const preset = await db.presets.add(presetData);
    presets.update(p => [...p, preset]);
    log.info('Preset created', { presetId: preset.id, name: preset.name });
    markPresetDirty(preset.id);
    return preset;
  },

  /**
   * Update an existing preset
   */
  async updatePreset(presetId: string, updates: UpdatePresetInput): Promise<Preset> {
    const updated = await db.presets.update(presetId, updates);
    presets.update(p => p.map(pr => pr.id === presetId ? updated : pr));
    // Also update selectedPreset if it's the one being edited
    const current = get(selectedPreset);
    if (current?.id === presetId) {
      selectedPreset.set(updated);
    }
    log.info('Preset updated', { presetId });
    markPresetDirty(presetId);
    return updated;
  },

  /**
   * Delete a preset and its associated templates
   */
  async deletePreset(presetId: string): Promise<void> {
    // Look up preset name before deletion for Git dir computation
    const presetRef = get(presets).find(p => p.id === presetId);

    await db.presets.delete(presetId);
    presets.update(p => p.filter(pr => pr.id !== presetId));
    const current = get(selectedPreset);
    if (current?.id === presetId) {
      selectedPreset.set(null);
      presetMode.set(false);
    }
    log.info('Preset deleted', { presetId });

    // Delete from Git (fire-and-forget)
    if (presetRef) {
      const slug = presetRef.name.toLowerCase().normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      deletePresetFromGit(`${presetId}_${slug}`).catch(e =>
        log.error('Failed to delete preset from Git', { presetId, error: (e as Error).message })
      );
    }
  },

  /**
   * Select a preset for editing
   * Loads the preset's templates into the templates store
   */
  async selectPreset(preset: Preset | null): Promise<void> {
    selectedPreset.set(preset);
    if (preset) {
      presetMode.set(true);
      // Load preset templates into the main templates store
      const tpls = await db.templates.getByPreset(preset.id);
      templates.set(tpls);
    } else {
      presetMode.set(false);
      templates.set([]);
    }
  },

  /**
   * Load templates for a specific preset
   */
  async loadPresetTemplates(presetId: string): Promise<Template[]> {
    const tpls = await db.templates.getByPreset(presetId);
    log.debug('Preset templates loaded', { presetId, count: tpls.length });
    return tpls;
  },

  /**
   * Apply a preset to a project: copy configs, merge metadata defaults
   */
  async applyPresetToProject(presetId: string, projectId: string): Promise<void> {
    const preset = await db.presets.get(presetId);
    if (!preset) throw new Error(`Preset not found: ${presetId}`);

    // 1. Copy config templates from preset to project
    const presetTpls = await db.templates.getByPreset(presetId);
    const projectTpls = await db.templates.getByProject(projectId);
    const configFilenames = ['mapping.json', 'typography.json', 'pagelayout.json', 'grep.json', 'export.json'];

    for (const configFilename of configFilenames) {
      const presetConfig = presetTpls.find(t => t.filename === configFilename);
      if (!presetConfig) continue;

      const existingProject = projectTpls.find(t => t.filename === configFilename);
      if (existingProject) {
        // Update existing project config with preset values
        await db.templates.update(existingProject.id, { content: presetConfig.content });
      } else {
        // Create new config template in the project
        await db.templates.add({
          projectId,
          filename: configFilename,
          content: presetConfig.content,
          templateType: presetConfig.templateType,
          templateSection: presetConfig.templateSection,
          mimeType: presetConfig.mimeType,
          size: presetConfig.size,
        });
      }
    }

    // 2. Update general.json with presetId/presetName
    const generalConfig = presetTpls.find(t => t.filename === 'general.json');
    let generalData: Record<string, any> = {};
    if (generalConfig?.content) {
      try { generalData = JSON.parse(generalConfig.content); } catch { /* ignore */ }
    }
    // Also check existing project general.json
    const existingGeneral = projectTpls.find(t => t.filename === 'general.json');
    if (existingGeneral?.content) {
      try { generalData = { ...JSON.parse(existingGeneral.content), ...generalData }; } catch { /* ignore */ }
    }
    generalData.presetId = presetId;
    generalData.presetName = preset.name;
    const generalContent = JSON.stringify(generalData, null, '\t');
    if (existingGeneral) {
      await db.templates.update(existingGeneral.id, { content: generalContent });
    } else {
      await db.templates.add({
        projectId,
        filename: 'general.json',
        content: generalContent,
        templateType: 'config',
        templateSection: 'config',
        mimeType: 'application/json',
        size: new Blob([generalContent]).size,
      });
    }

    // 3. Merge metadata defaults from preset (fill empty fields only)
    if (preset.metadata) {
      try {
        const presetMeta = JSON.parse(preset.metadata);
        const project = await db.projects.get(projectId);
        if (project) {
          let projectMeta: Record<string, any> = {};
          if (project.metadata) {
            // Parse existing YAML metadata (simple key-value)
            for (const line of project.metadata.split('\n')) {
              const match = line.match(/^(\w[\w-]*)\s*:\s*(.+)$/);
              if (match) {
                projectMeta[match[1]] = match[2].replace(/^['"]|['"]$/g, '');
              }
            }
          }
          // Merge: only fill empty fields from preset defaults
          let metadataChanged = false;
          const simpleFields = ['publisher', 'place', 'serie', 'rights', 'accessibilitySummary'];
          for (const field of simpleFields) {
            if (!projectMeta[field] && presetMeta[field]) {
              projectMeta[field] = presetMeta[field];
              metadataChanged = true;
            }
          }
          // For arrays: merge if project has empty/default arrays
          const arrayFields = ['accessModes', 'accessModeSufficient', 'accessibilityFeatures', 'accessibilityHazards'];
          for (const field of arrayFields) {
            if (presetMeta[field]?.length && (!projectMeta[field] || projectMeta[field] === '')) {
              // These will be picked up when metadata is reloaded from YAML
              metadataChanged = true;
            }
          }

          if (metadataChanged && project.metadata) {
            // Re-serialize to YAML (append new fields)
            let yaml = project.metadata;
            for (const field of simpleFields) {
              if (presetMeta[field] && !project.metadata.includes(`${field}:`)) {
                yaml += `\n${field}: "${presetMeta[field]}"`;
              }
            }
            await db.projects.update(projectId, { metadata: yaml });
            // Refresh the project in the store
            const updated = await db.projects.get(projectId);
            if (updated) {
              projects.update(list => list.map(p => p.id === projectId ? updated : p));
              const sel = get(selectedProject);
              if (sel?.id === projectId) selectedProject.set(updated);
            }
          }
        }
      } catch (e) {
        log.warn('Failed to merge preset metadata', { error: e });
      }
    }

    // 4. Reload project templates
    const updatedTpls = await db.templates.getByProject(projectId);
    templates.set(updatedTpls);

    log.info('Preset applied to project', { presetId, projectId, presetName: preset.name });
    markProjectDirty(projectId, 'config');
  },

  /**
   * Import a project from an AutomaticBook ZIP file
   */
  async importProjectFromZip(file: File): Promise<Project> {
    log.info('Importing project from ZIP', { filename: file.name });

    const zip = await JSZip.loadAsync(file);

    // 1. Find manifest.yaml at root
    const manifestFile = zip.file('manifest.yaml');
    if (!manifestFile) {
      throw new Error('manifest.yaml not found in ZIP');
    }
    const manifestContent = await manifestFile.async('string');

    // 2. Parse manifest (simple YAML line-by-line)
    const manifestLines = manifestContent.split('\n');
    let projectTitle = file.name.replace(/\.zip$/i, '');
    const textEntries: Array<{ file: string; type?: string }> = [];
    let currentSection = '';
    let currentItem: Record<string, string> | null = null;

    for (const line of manifestLines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) continue;

      // Top-level keys
      if (!line.startsWith(' ') && trimmed.includes(':')) {
        const [key, ...rest] = trimmed.split(':');
        const val = rest.join(':').trim();
        if (key === 'texts') {
          currentSection = 'texts';
          continue;
        } else {
          currentSection = '';
        }
      }

      if (currentSection === 'texts') {
        if (trimmed.startsWith('- file:')) {
          if (currentItem) textEntries.push({ file: currentItem.file, type: currentItem.type });
          currentItem = { file: trimmed.replace('- file:', '').trim() };
        } else if (trimmed.startsWith('type:') && currentItem) {
          currentItem.type = trimmed.replace('type:', '').trim();
        }
      }
    }
    if (currentItem) textEntries.push({ file: currentItem.file, type: currentItem.type });

    // 3. Read metadata.yaml
    const metadataFile = zip.file('metadata.yaml');
    let metadata = '';
    if (metadataFile) {
      metadata = await metadataFile.async('string');
      // Extract title from metadata if available
      const titleMatch = metadata.match(/^title:\s*"?([^"\n]+)"?\s*$/m);
      if (titleMatch) {
        projectTitle = titleMatch[1].trim();
      }
      // Also check for structured title block
      const structuredTitleMatch = metadata.match(/title:\s*\n\s+-\s*type:\s*main\s*\n\s+text:\s*"?([^"\n]+)"?/);
      if (structuredTitleMatch) {
        projectTitle = structuredTitleMatch[1].trim();
      }
    }

    // 4. Create project
    const project = await this.createProject({
      title: projectTitle,
      projectType: 'book',
      metadata: metadata
    });

    const projectId = project.id;

    // 5. Helper: detect documentType from filename
    const detectDocType = (filename: string): DocumentType | null => {
      const base = filename.replace(/\.md$/i, '').replace(/^\d+-/, '');
      const typeMap: Record<string, DocumentType> = {
        'titlepage': 'titlepage',
        'halftitlepage': 'halftitlepage',
        'copyright-page': 'copyright-page',
        'dedication': 'dedication',
        'foreword': 'foreword',
        'preface': 'preface',
        'introduction': 'introduction',
        'prologue': 'prologue',
        'acknowledgments': 'acknowledgments',
        'abstract': 'abstract',
        'frontispiece': 'frontispiece',
        'seriespage': 'seriespage',
        'bibliography': 'bibliography',
        'index': 'index',
        'colophon': 'colophon',
        'appendix': 'appendix',
        'conclusion': 'conclusion',
        'annexe': 'annexe',
        'postface': 'postface',
      };
      for (const [key, type] of Object.entries(typeMap)) {
        if (base === key || base.startsWith(key + '-')) return type;
      }
      if (/^chapter/.test(base) || /^ch\d/.test(base) || /^part/.test(base)) {
        return base.startsWith('part') ? 'part' : 'chapter';
      }
      return null;
    };

    // 6. Create documents from texts/
    for (let idx = 0; idx < textEntries.length; idx++) {
      const entry = textEntries[idx];
      const textFile = zip.file(entry.file);
      if (!textFile) continue;

      const content = await textFile.async('string');
      const basename = entry.file.split('/').pop() || `document-${idx}.md`;
      const title = basename.replace(/\.md$/i, '').replace(/^\d+-/, '').replace(/-/g, ' ');
      const documentSection = (entry.type as DocumentSection) || 'bodymatter';
      const documentType = detectDocType(basename);

      await db.documents.add({
        projectId,
        title,
        filename: basename,
        status: 'done',
        content,
        documentType,
        documentSection,
        order: idx,
      });
    }

    // 7. Import images from assets/
    const assetsFolder = zip.folder('assets');
    if (assetsFolder) {
      const assetFiles = Object.keys(zip.files).filter(p => p.startsWith('assets/') && !p.endsWith('/'));
      for (const path of assetFiles) {
        const assetFile = zip.file(path);
        if (!assetFile) continue;

        const filename = path.split('/').pop() || 'image';
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        const mimeMap: Record<string, string> = {
          'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'png': 'image/png',
          'gif': 'image/gif', 'svg': 'image/svg+xml', 'webp': 'image/webp',
        };
        const mimeType = mimeMap[ext] || 'application/octet-stream';
        const base64 = await assetFile.async('base64');
        const dataUrl = `data:${mimeType};base64,${base64}`;
        const isCover = filename.toLowerCase().includes('cover') || path.toLowerCase().includes('cover');

        await db.images.add({
          projectId,
          filename,
          mimeType,
          dataUrl,
          size: base64.length,
          imageSection: isCover ? 'cover' : 'images',
        });
      }
    }

    // 8. Import config files → Templates
    const configMap: Array<{ zipPath: string; filename: string }> = [
      { zipPath: 'config/mapping.json', filename: 'mapping.json' },
      { zipPath: 'config/typography.json', filename: 'typography.json' },
      { zipPath: 'config/pagelayout.json', filename: 'pagelayout.json' },
      { zipPath: 'config/grep.json', filename: 'grep.json' },
      { zipPath: 'config/export.json', filename: 'export.json' },
      { zipPath: 'config/general.json', filename: 'general.json' },
    ];
    for (const cfg of configMap) {
      const cfgFile = zip.file(cfg.zipPath);
      if (!cfgFile) continue;
      const content = await cfgFile.async('string');
      await db.templates.add({
        projectId,
        filename: cfg.filename,
        templateType: 'config',
        templateSection: 'config',
        mimeType: 'application/json',
        content,
        size: content.length,
      });
    }

    // 9. Import template files
    const templateMappings: Array<{
      prefix: string;
      templateType: string;
      templateSection: string;
      mimeType: string;
      binary?: boolean;
    }> = [
      { prefix: 'template/indesign/', templateType: 'indesign', templateSection: 'indesign', mimeType: 'application/octet-stream', binary: true },
      { prefix: 'template/epub/style/', templateType: 'css', templateSection: 'epub3', mimeType: 'text/css' },
      { prefix: 'template/epub/fonts/', templateType: 'font', templateSection: 'fonts', mimeType: 'font/ttf', binary: true },
      { prefix: 'template/epub/lua/', templateType: 'lua', templateSection: 'luafilters', mimeType: 'text/x-lua' },
      { prefix: 'template/csl/', templateType: 'csl', templateSection: 'csl', mimeType: 'application/xml' },
      { prefix: 'template/preparo/', templateType: 'preparo', templateSection: 'preparo', mimeType: 'text/markdown' },
    ];

    for (const mapping of templateMappings) {
      const files = Object.keys(zip.files).filter(p => p.startsWith(mapping.prefix) && !p.endsWith('/'));
      for (const path of files) {
        const tplFile = zip.file(path);
        if (!tplFile) continue;

        const filename = path.split('/').pop() || 'template';
        const content = mapping.binary
          ? await tplFile.async('base64')
          : await tplFile.async('string');

        await db.templates.add({
          projectId,
          filename,
          templateType: mapping.templateType as any,
          templateSection: mapping.templateSection as any,
          mimeType: mapping.mimeType,
          content,
          size: content.length,
        });
      }
    }

    // 10. Load docs and templates into stores, select project
    const allDocs = await db.documents.getByProject(projectId);
    docs.set(allDocs);
    const allTemplates = await db.templates.getByProject(projectId);
    templates.set(allTemplates);
    const allImages = await db.images.getByProject(projectId);
    images.set(allImages);

    // Add project to projects list and select it
    projects.update(p => [...p, project]);
    selectedProject.set(project);

    log.info('Project imported from ZIP', { projectId, title: projectTitle, docs: allDocs.length, templates: allTemplates.length });
    markProjectDirty(projectId, 'metadata');
    return project;
  },

  /**
   * Reset application
   */
  reset(): void {
    batchUpdates(() => {
      selectedProject.set(null);
      selectedDoc.set(null);
      projects.set([]);
      docs.set([]);
      images.set([]);
      templates.set([]);
      presets.set([]);
      selectedPreset.set(null);
      presetMode.set(false);
      searchQuery.set('');
      sortColumn.set('title');
      sortOrder.set('asc');
      loadingState.set({ projects: false, docs: false, doc: false });
      errors.set({ projects: null, docs: null, doc: null });
    });

    log.info('App state reset');
  },
};

// ============================================================================
// PERSISTENCE FUNCTIONS (IndexedDB)
// ============================================================================

/**
 * Initialize database and migrate localStorage data
 */
export async function initializeDatabase(): Promise<boolean> {
  try {
    await db.init();
    log.info('IndexedDB ready');

    const { migrated, stats } = await migrateFromLocalStorage(db);
    if (migrated) {
      log.info('Migrated from localStorage', { ...stats });
    }

    // Non-blocking orphan cleanup — runs after init, doesn't block app startup
    db.cleanOrphans().catch(err => {
      log.warn('Orphan cleanup failed (non-critical)', { error: (err as Error).message });
    });

    return true;
  } catch (error) {
    log.error('Failed to initialize database', { error });
    throw error;
  }
}

/**
 * Fetch projects from IndexedDB
 */
async function fetchProjects(): Promise<Project[]> {
  try {
    const projectsData = await db.projects.getAll();
    log.debug('Fetched projects from IndexedDB', { count: projectsData.length });
    return projectsData;
  } catch (error) {
    log.error('Failed to fetch projects', { error });
    return [];
  }
}

/**
 * Fetch documents for a project from IndexedDB
 */
async function fetchDocs(projectId: string | null = null): Promise<Document[]> {
  try {
    if (!projectId) {
      return [];
    }
    const documents = await db.documents.getByProject(projectId);
    log.debug('Fetched documents from IndexedDB', { projectId, count: documents.length });
    return documents;
  } catch (error) {
    log.error('Failed to fetch documents', { projectId, error });
    return [];
  }
}

/**
 * Create a project in IndexedDB
 */
async function createProjectAPI(projectData: CreateProjectInput): Promise<Project> {
  try {
    const newProject = await db.projects.add(projectData);
    log.debug('Project created in IndexedDB', { projectId: newProject.id });
    return newProject;
  } catch (error) {
    log.error('Failed to create project in IndexedDB', { error });
    throw error;
  }
}

/**
 * Update a project in IndexedDB
 */
async function updateProjectAPI(projectId: string, updates: UpdateProjectInput): Promise<Project> {
  try {
    const updatedProject = await db.projects.update(projectId, updates);
    log.debug('Project updated in IndexedDB', { projectId });
    return updatedProject;
  } catch (error) {
    log.error('Failed to update project in IndexedDB', { projectId, error });
    throw error;
  }
}

/**
 * Delete a project from IndexedDB
 */
async function deleteProjectAPI(projectId: string): Promise<void> {
  try {
    await db.projects.delete(projectId);
    log.debug('Project deleted from IndexedDB', { projectId });
  } catch (error) {
    log.error('Failed to delete project from IndexedDB', { projectId, error });
    throw error;
  }
}

/**
 * Create a document in IndexedDB
 */
async function createDocAPI(docData: CreateDocumentInput): Promise<Document> {
  try {
    const newDoc = await db.documents.add(docData);
    log.debug('Document created in IndexedDB', { docId: newDoc.id });
    return newDoc;
  } catch (error) {
    log.error('Failed to create document in IndexedDB', { error });
    throw error;
  }
}

/**
 * Update a document in IndexedDB
 */
async function updateDocAPI(docId: string, updates: UpdateDocumentInput): Promise<Document> {
  try {
    const updatedDoc = await db.documents.update(docId, updates);
    log.debug('Document updated in IndexedDB', { docId });
    return updatedDoc;
  } catch (error) {
    log.error('Failed to update document in IndexedDB', { docId, error });
    throw error;
  }
}

/**
 * Delete a document from IndexedDB
 */
async function deleteDocAPI(docId: string): Promise<void> {
  try {
    await db.documents.delete(docId);
    log.debug('Document deleted from IndexedDB', { docId });
  } catch (error) {
    log.error('Failed to delete document from IndexedDB', { docId, error });
    throw error;
  }
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Stores
  selectedProject,
  selectedDoc,
  projects,
  docs,
  images,
  templates,
  searchQuery,
  sortColumn,
  sortOrder,
  uiState,
  loadingState,
  errors,

  // Derived stores
  filteredDocs,
  sortedDocs,
  projectDocs,
  projectDocsOrdered,
  docStatistics,
  isLoading,
  hasErrors,

  // Actions
  ...appActions,
};
